import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import { Switch } from "@/clientWeb/components/ui/switch";
import { cn } from "@vc/lib/utils";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import { AUDIO_PRESETS, useDeviceEnumeration, useDevicesStore } from "@/features/devices";
import type { AudioPresetName } from "@/features/devices";

interface AudioSettingsProps {
  // Callbacks can be added here when needed
}

export function AudioSettings(_props: AudioSettingsProps) {
  const t = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(true);

  // Real devices, bound to the same store the lobby and control bar use.
  //
  // These were two hardcoded strings in useState — "Microphone Array (Realtek
  // High Definition Audio)" and a Realtek speaker — so the panel named devices
  // that had nothing to do with the machine it was running on, and picking one
  // changed a local variable and nothing else.
  const { mics, speakers } = useDeviceEnumeration();
  const selectedMicId = useDevicesStore((s) => s.selectedMicId);
  const selectedSpeakerId = useDevicesStore((s) => s.selectedSpeakerId);
  const selectMic = useDevicesStore((s) => s.selectMic);
  const selectSpeaker = useDevicesStore((s) => s.selectSpeaker);

  const micLabel =
    mics.find((d) => d.deviceId === selectedMicId)?.label || t("controlBar.selectMic");
  const speakerLabel =
    speakers.find((d) => d.deviceId === selectedSpeakerId)?.label ||
    t("controlBar.selectSpeaker");
  // The audio preset, and the two constraints it sets. These three dropdowns
  // sat commented out below for want of anything to write to; they now write to
  // the devices store, which capture and the Opus encoder both read.
  const audioPreset = useDevicesStore((s) => s.audioPreset);
  const audioFormat = useDevicesStore((s) => s.audioFormat);
  const selectAudioPreset = useDevicesStore((s) => s.selectAudioPreset);
  const setAudioFormat = useDevicesStore((s) => s.setAudioFormat);
  const presetLabel =
    audioPreset === "custom" ? t("controlBar.customAudio") : AUDIO_PRESETS[audioPreset].label;
  // The browser's real constraints, applied to the live microphone track.
  const audioProcessing = useDevicesStore((s) => s.audioProcessing);
  const setAudioProcessing = useDevicesStore((s) => s.setAudioProcessing);
  // Separate from the browser constraints above: this is the RNNoise denoiser,
  // not the platform's own filter, and the two are mutually exclusive.
  const noiseCancellation = useDevicesStore((s) => s.noiseCancellation);
  const setNoiseCancellation = useDevicesStore((s) => s.setNoiseCancellation);

  return (
    <div className="flex-1 max-h-[400px] overflow-y-auto overflow-x-hidden px-2 space-y-5">
      {/* Select Mic */}
      <div className="space-y-2">
        <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
          {t("controlBar.selectMic")}
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2 text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
              <span className="truncate pr-4">{micLabel}</span>
              <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
            {mics.length === 0 ? (
              <DropdownMenuItem disabled>{t("controlBar.selectMic")}</DropdownMenuItem>
            ) : (
              mics.map((d) => (
                <DropdownMenuItem
                  key={d.deviceId}
                  onClick={() => selectMic(d.deviceId)}
                  className="truncate"
                >
                  {d.label || t("controlBar.selectMic")}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Select Speaker */}
      <div className="space-y-2">
        <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
          {t("controlBar.selectSpeaker")}
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
              <span className="truncate pr-4">{speakerLabel}</span>
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
            {speakers.length === 0 ? (
              <DropdownMenuItem disabled>{t("controlBar.selectSpeaker")}</DropdownMenuItem>
            ) : (
              speakers.map((d) => (
                <DropdownMenuItem
                  key={d.deviceId}
                  onClick={() => selectSpeaker(d.deviceId)}
                  className="truncate"
                >
                  {d.label || t("controlBar.selectSpeaker")}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Advanced Audio settings - Collapsible */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="flex items-center justify-between w-full py-2 text-sm font-semibold text-slate-800 dark:text-foreground hover:text-slate-600 dark:hover:text-foreground/80 transition-colors"
        >
          <span>{t("controlBar.advancedAudioSettings")}</span>
          {advancedOpen ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </button>

        {advancedOpen && (
          <div className="space-y-5 pt-3">
            {/* Noise cancellation — RNNoise over the published microphone.
                Turning it on switches the browser's own noiseSuppression off:
                two suppressors in series sound worse than either alone. */}
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.noiseCancellation")}</span>
              <Switch
                checked={noiseCancellation}
                onCheckedChange={setNoiseCancellation}
              />
            </div>

            {/* Noise suppression — the browser's own filter. Disabled while
                RNNoise is on, because it is genuinely not in use then; leaving
                it toggleable would offer a control that silently does nothing. */}
            <div className="flex items-center justify-between py-1">
              <span
                className={cn(
                  "text-sm text-slate-700 dark:text-foreground",
                  noiseCancellation && "opacity-50",
                )}
              >
                {t("controlBar.noiseSuppression")}
              </span>
              <Switch
                checked={noiseCancellation ? false : audioProcessing.noiseSuppression}
                disabled={noiseCancellation}
                onCheckedChange={(v) => setAudioProcessing({ noiseSuppression: v })}
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.echoCancellation")}</span>
              <Switch
                checked={audioProcessing.echoCancellation}
                onCheckedChange={(v) => setAudioProcessing({ echoCancellation: v })}
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.autoGainControl")}</span>
              <Switch
                checked={audioProcessing.autoGainControl}
                onCheckedChange={(v) => setAudioProcessing({ autoGainControl: v })}
              />
            </div>

            {/* Audio preset — capture constraints and Opus settings as one
                choice, so the two cannot drift apart. "Custom" appears once the
                switches above no longer match any preset, rather than the panel
                naming audio you are not sending. */}
            <div className="space-y-2 pt-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectAudioPreset")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2 text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span className="truncate pr-4">{presetLabel}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
                  {(Object.keys(AUDIO_PRESETS) as AudioPresetName[]).map((name) => (
                    <DropdownMenuItem
                      key={name}
                      onClick={() => selectAudioPreset(name)}
                      className="flex flex-col items-start gap-0.5"
                    >
                      <span>{AUDIO_PRESETS[name].label}</span>
                      <span className="text-xs text-slate-500 dark:text-muted-foreground whitespace-normal">
                        {AUDIO_PRESETS[name].description}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {/* Opus settings are read once, when the producer is created, so
                  a change mid-call reaches the room on the next one. Saying so
                  beats a control that looks instant and is not. */}
              <p className="text-xs text-slate-500 dark:text-muted-foreground">
                {t("controlBar.audioPresetEncoderNote")}
              </p>
            </div>

            {/* Sample rate. Not `exact`: a device that cannot do 24 kHz should
                be opened at what it can and resampled, not refused. */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectAudioSampleRate")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2 text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span className="truncate pr-4">{`${audioFormat.sampleRate / 1000} kHz`}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
                  {[48000, 44100, 32000, 24000, 16000, 8000].map((rate) => (
                    <DropdownMenuItem key={rate} onClick={() => setAudioFormat({ sampleRate: rate })}>
                      {`${rate / 1000} kHz`}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectAudioChannelCount")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2 text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span className="truncate pr-4">
                      {audioFormat.channelCount === 2
                        ? t("controlBar.channelStereo")
                        : t("controlBar.channelMono")}
                    </span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
                  <DropdownMenuItem onClick={() => setAudioFormat({ channelCount: 1 })}>
                    {t("controlBar.channelMono")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAudioFormat({ channelCount: 2 })}>
                    {t("controlBar.channelStereo")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* The noise-threshold slider and the bare Opus switches below are
                still parked.
                None of it was wired to anything: the threshold slider had no
                state at all, the three dropdowns wrote to local useState the
                encoder never read, and the Opus switches were bare
                defaultChecked with no handler. They read as working controls
                and changed nothing about the published audio. Kept here rather
                than deleted so they can come back with the plumbing behind
                them; the i18n keys are still in all-translations.json.

                === Noise threshold slider ===
            <div className="space-y-3 py-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.noiseThreshold")}</span>
              </div>
              <div className="relative pt-1">
                <input
                  type="range"
                  min="-100"
                  max="0"
                  defaultValue="-100"
                  className="w-full h-1 bg-slate-200 dark:bg-muted rounded-lg appearance-none cursor-pointer accent-[#2bb5d8]"
                />
                <div className="flex justify-between text-xs text-slate-500 dark:text-muted-foreground mt-1">
                  <span>-100 dB</span>
                </div>
              </div>
            </div>

                === Audio preset ===
            <div className="space-y-2 pt-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectAudioPreset")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-[#2bb5d8] cursor-pointer">
                    <span className="truncate pr-4">{audioPreset}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
                  <DropdownMenuItem onClick={() => setAudioPreset("Conference audio")}>
                    Conference audio
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAudioPreset("Speech audio")}>
                    Speech audio
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAudioPreset("Music audio")}>
                    Music audio
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

                === Audio sample rate ===
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectAudioSampleRate")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-[#2bb5d8] cursor-pointer">
                    <span className="truncate pr-4">{sampleRate}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
                  <DropdownMenuItem onClick={() => setSampleRate("48 kHz")}>48 kHz</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSampleRate("44.1 kHz")}>44.1 kHz</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSampleRate("32 kHz")}>32 kHz</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSampleRate("24 kHz")}>24 kHz</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSampleRate("16 kHz")}>16 kHz</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSampleRate("8 kHz")}>8 kHz</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

                === Audio channel count ===
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectAudioChannelCount")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-[#2bb5d8] cursor-pointer">
                    <span className="truncate pr-4">{channelCount}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
                  <DropdownMenuItem onClick={() => setChannelCount("1 (mono)")}>1 (mono)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setChannelCount("2 (stereo)")}>2 (stereo)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

                === Enable Opus DTX ===
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.enableOpusDTX")}</span>
              <Switch defaultChecked />
            </div>

                === Enable Opus FEC ===
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.enableOpusFEC")}</span>
              <Switch defaultChecked />
            </div>

                === Enable OPUS details ===
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.enableOpusDetails")}</span>
              <Switch />
            </div>
            */}
          </div>
        )}
      </div>
    </div>
  );
}

